import { useState } from "react";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AuthField, AuthError, SubmitButton, useSubmitState } from "@/components/auth/AuthParts";

const EMPTY = { current: "", next: "", confirm: "" };

function ChangePasswordForm({ onDone }) {
  const [form, setForm] = useState(EMPTY);
  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const { error, loading, submit } = useSubmitState(async () => {
    if (form.next !== form.confirm) throw new Error("New passwords don't match");
    await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
    toast.success("Password updated");
    onDone();
  });

  return (
    <form onSubmit={submit} className="mt-4 border-2 border-black p-4 md:p-6 space-y-4 bg-white max-w-[480px]" data-testid="change-password-form">
      <AuthField label="Current Password" testId="current-password" type="password" autoComplete="current-password" value={form.current} onChange={set("current")} />
      <AuthField label="New Password" testId="new-password" type="password" minLength={6} autoComplete="new-password" value={form.next} onChange={set("next")} />
      <AuthField label="Confirm New Password" testId="confirm-new-password" type="password" minLength={6} autoComplete="new-password" value={form.confirm} onChange={set("confirm")} />
      <AuthError testId="change-password-error" message={error} />
      <div className="flex gap-3">
        <SubmitButton testId="change-password-submit" loading={loading}>Update Password</SubmitButton>
        <button type="button" data-testid="change-password-cancel" onClick={onDone} className="border-2 border-black px-4 py-3 font-mono-data uppercase text-sm bg-white hover:bg-gray-100">Cancel</button>
      </div>
    </form>
  );
}

export function AccountSecurity({ user }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="mt-10" data-testid="account-security">
      <div className="flex items-center justify-between border-b-2 border-black pb-2">
        <h2 className="font-display text-2xl uppercase">/ Account Security</h2>
        {user.has_password && !open && (
          <button data-testid="change-password-btn" onClick={() => setOpen(true)} className="border-2 border-black px-4 py-2 font-mono-data text-xs uppercase bg-white hover:bg-black hover:text-white flex items-center gap-1">
            <KeyRound size={14} /> Change Password
          </button>
        )}
      </div>
      {!user.has_password && (
        <p data-testid="google-account-note" className="font-mono-data text-xs uppercase text-gray-600 mt-3">You sign in with Google, so there's no password to change here.</p>
      )}
      {user.has_password && !open && (
        <p className="font-mono-data text-xs uppercase text-gray-600 mt-3">Use a password you don't use on any other site.</p>
      )}
      {open && <ChangePasswordForm onDone={() => setOpen(false)} />}
    </section>
  );
}
